/**
 * Helper functions and constants for reusable dynamic fields
 * (EnhancedConfirmationDialog, TabsManager, AccordionManager, InfoStackPanel)
 */

/**
 * Field type constants
 */
export const FIELD_TYPES = {
    TEXT: 'text',
    NUMBER: 'number',
    EMAIL: 'email',
    SELECT: 'select',
    DROPDOWN: 'dropdown',
    BUTTON: 'button',
    RADIO: 'radio',
    FILE: "file",
    LIST: "list",
    RICH_LIST: "rich_list",
    DIVIDER: "divider",
    HTML: "html",
    LINK: "link",
    CHECKBOX: "checkbox",
    SWITCH: "switch",
    DATE: "date",
};

/**
 * Styling constants for readonly fields
 */
export const READONLY_FIELD_STYLES = {
    backgroundColor: 'action.disabled',
    '& input': {
        cursor: 'default',
    }
};

/**
 * Helper text styling for error messages
 */
export const HELPER_TEXT_STYLES = {
    fontStyle: 'italic'
};

/**
 * Builds validation rules for React Hook Form based on field configuration
 *
 * @param {Object} field - Field configuration object
 * @param {boolean} field.required - Whether the field is required
 * @param {boolean} field.multiple - Whether multiple values can be selected
 * @param {RegExp|string} field.regex - Validation regex pattern
 * @param {string} field.label - Field label for error messages
 * @param {string} field.errorMessage - Custom error message for required validation
 * @param {string} field.regexErrorMessage - Custom error message for regex validation
 * @returns {Object} React Hook Form validation rules object
 *
 * @example
 * const rules = buildValidationRules({
 *   required: true,
 *   label: 'Email',
 *   regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
 *   regexErrorMessage: 'Invalid email format'
 * });
 */
export const buildValidationRules = (field) => {
    const { required, multiple, regex, label, errorMessage, regexErrorMessage } = field;
    const rules = {};

    if (required) {
        if (multiple) {
            rules.validate = (value) => {
                if (!value || !Array.isArray(value) || value.length === 0) {
                    return errorMessage || `Please select at least one ${label.toLowerCase()}`;
                }
                return true;
            };
        } else {
            rules.required = errorMessage || `${label} is required`;
        }
    }

    if (regex) {
        rules.pattern = {
            value: typeof regex === 'string' ? new RegExp(regex) : regex,
            message: regexErrorMessage || `Invalid format for ${label}`,
        };
    }

    return rules;
};

/**
 * Gets the appropriate styling for readonly fields
 *
 * @param {boolean} readOnly - Whether the field is readonly
 * @returns {Object|undefined} Style object or undefined
 */
export const getReadOnlyStyles = (readOnly) => {
    return readOnly ? READONLY_FIELD_STYLES : undefined;
};
